import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

// ============ TESTIMONIALS sub-editor ============
function TestimonialItem({ item, onChange, onRemove }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={item.name || ""}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          placeholder="Imie i nazwisko"
        />
        <Input
          type="number"
          min={1}
          max={5}
          value={item.rating ?? 5}
          onChange={(e) => onChange({ ...item, rating: Math.max(1, Math.min(5, parseInt(e.target.value) || 5)) })}
          placeholder="Ocena 1-5"
        />
      </div>
      <Textarea
        rows={2}
        value={item.text || ""}
        onChange={(e) => onChange({ ...item, text: e.target.value })}
        placeholder="Tresc opinii"
      />
      <Button variant="outline" size="sm" onClick={onRemove} className="text-rose-600 border-rose-200 hover:bg-rose-50">
        <Trash2 className="w-3 h-3 mr-1" /> Usun opinie
      </Button>
    </div>
  );
}

export default function BlockEditor({ block, onChange }) {
  const set = (key, value) => onChange({ ...block, data: { ...block.data, [key]: value } });

  switch (block.type) {
    case "hero":
      return (
        <div className="space-y-3">
          <div>
            <Label>Tytul</Label>
            <Input
              value={block.data.title || ""}
              onChange={(e) => set("title", e.target.value)}
              placeholder="np. Salon Bella"
            />
          </div>
          <div>
            <Label>Podtytul</Label>
            <Input
              value={block.data.subtitle || ""}
              onChange={(e) => set("subtitle", e.target.value)}
              placeholder="Krotki opis pod tytulem"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="ub"
              checked={!!block.data.use_business_photo}
              onCheckedChange={(v) => set("use_business_photo", !!v)}
            />
            <Label htmlFor="ub" className="text-sm">
              Uzyj zdjecia firmy (z katalogu)
            </Label>
          </div>
          {!block.data.use_business_photo && (
            <div>
              <Label>URL zdjecia</Label>
              <Input
                value={block.data.photo_url || ""}
                onChange={(e) => set("photo_url", e.target.value)}
                placeholder="https://..."
              />
            </div>
          )}
        </div>
      );

    case "about":
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input value={block.data.heading || ""} onChange={(e) => set("heading", e.target.value)} />
          </div>
          <div>
            <Label>Tresc</Label>
            <Textarea
              rows={6}
              value={block.data.body || ""}
              onChange={(e) => set("body", e.target.value)}
              placeholder="Opis firmy, oferty, podejscia"
            />
          </div>
        </div>
      );

    case "cta":
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input value={block.data.heading || ""} onChange={(e) => set("heading", e.target.value)} />
          </div>
          <div>
            <Label>Podtytul</Label>
            <Input value={block.data.subheading || ""} onChange={(e) => set("subheading", e.target.value)} />
          </div>
          <div>
            <Label>Tekst przycisku</Label>
            <Input
              value={block.data.button_text || ""}
              onChange={(e) => set("button_text", e.target.value)}
            />
          </div>
          <div>
            <Label>Link (tel: lub mailto: lub https:)</Label>
            <Input
              value={block.data.button_link || block.data.button_url || ""}
              onChange={(e) => {
                set("button_link", e.target.value);
                set("button_url", e.target.value);
              }}
              placeholder="tel:+48..."
            />
          </div>
        </div>
      );

    case "services":
    case "why_us": {
      const items = block.data.items || [];
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input value={block.data.heading || ""} onChange={(e) => set("heading", e.target.value)} />
          </div>
          <div>
            <Label className="mb-2 block">Pozycje (po jednej na linie)</Label>
            <Textarea
              rows={8}
              value={items.join("\n")}
              onChange={(e) => set("items", e.target.value.split("\n").filter((s) => s.length))}
              placeholder={
                block.type === "services"
                  ? "Strzyzenie damskie 100 zl\nKoloryzacja od 180 zl\nBalayage od 350 zl"
                  : "Doswiadczenie 10+ lat\nLokalizacja w centrum\nSzybkie terminy"
              }
            />
            <p className="text-xs text-slate-500 mt-1">{items.length} pozycji</p>
          </div>
        </div>
      );
    }

    case "gallery": {
      const photos = block.data.photos || [];
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input value={block.data.heading || ""} onChange={(e) => set("heading", e.target.value)} />
          </div>
          <div>
            <Label className="mb-2 block">Adresy URL zdjec (po jednym na linie)</Label>
            <Textarea
              rows={6}
              value={photos.join("\n")}
              onChange={(e) => set("photos", e.target.value.split("\n").filter((s) => s.length))}
              placeholder="https://...jpg&#10;https://...jpg"
            />
            <p className="text-xs text-slate-500 mt-1">{photos.length} zdjec</p>
          </div>
        </div>
      );
    }

    case "slotify":
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input
              value={block.data.heading || ""}
              onChange={(e) => set("heading", e.target.value)}
              placeholder="Zarezerwuj online"
            />
          </div>
          <div>
            <Label>Podtytul</Label>
            <Input value={block.data.subheading || ""} onChange={(e) => set("subheading", e.target.value)} />
          </div>
          <div>
            <Label>Slotify slug (org w Slotify)</Label>
            <Input
              value={block.data.slug || ""}
              onChange={(e) => set("slug", e.target.value)}
              placeholder="demo-beauty"
            />
          </div>
          <div>
            <Label>Wysokosc widgetu (px)</Label>
            <Input
              type="number"
              min={400}
              value={block.data.height || 850}
              onChange={(e) => set("height", parseInt(e.target.value) || 850)}
            />
          </div>
        </div>
      );

    case "parking_widget":
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input
              value={block.data.heading || ""}
              onChange={(e) => set("heading", e.target.value)}
              placeholder="Zarezerwuj miejsce parkingowe"
            />
          </div>
          <div>
            <Label>Podtytul</Label>
            <Input value={block.data.subheading || ""} onChange={(e) => set("subheading", e.target.value)} />
          </div>
          <div>
            <Label>URL widgetu</Label>
            <Input
              value={block.data.widget_url || ""}
              onChange={(e) => set("widget_url", e.target.value)}
              placeholder="https://slotify.dev.sorto.ai/widget/parking/lawica?lang=pl&theme=light"
            />
          </div>
          <div>
            <Label>Wysokosc widgetu (px)</Label>
            <Input
              type="number"
              min={200}
              value={block.data.height || 260}
              onChange={(e) => set("height", parseInt(e.target.value) || 260)}
            />
          </div>
        </div>
      );

    case "landing_premium": {
      const stats = block.data.stats || [];
      const steps = block.data.steps || [];
      const whyItems = block.data.why_us_items || [];
      const statsText = stats.map((s) => `${s.value || ""}|${s.label || ""}`).join("\n");
      const stepsText = steps.map((s) => `${s.title || ""}|${s.description || ""}`).join("\n");
      const whyText = whyItems.map((w) => `${w.icon || "shield"}|${w.title || ""}|${w.description || ""}`).join("\n");
      const parsePairs = (text, keys) =>
        text
          .split("\n")
          .filter((l) => l.trim().length)
          .map((line) => {
            const parts = line.split("|");
            const obj = {};
            keys.forEach((k, i) => { obj[k] = (parts[i] || "").trim(); });
            return obj;
          });
      return (
        <div className="space-y-3">
          <div>
            <Label>Eyebrow (mala etykieta nad naglowkiem)</Label>
            <Input
              value={block.data.eyebrow || ""}
              onChange={(e) => set("eyebrow", e.target.value)}
              placeholder="np. Parking lotniskowy — Poznan-Lawica"
            />
          </div>
          <div>
            <Label>Naglowek</Label>
            <Input
              value={block.data.headline || ""}
              onChange={(e) => set("headline", e.target.value)}
              placeholder="Zaparkuj u nas."
            />
          </div>
          <div>
            <Label>Wyroznienie w naglowku (opcjonalnie)</Label>
            <Input
              value={block.data.headline_emphasis || ""}
              onChange={(e) => set("headline_emphasis", e.target.value)}
              placeholder="Reszte zalatwi kierowca."
            />
          </div>
          <div>
            <Label>Podtytul</Label>
            <Textarea rows={2} value={block.data.subheadline || ""} onChange={(e) => set("subheadline", e.target.value)} />
          </div>
          <div>
            <Label>Tekst przycisku CTA</Label>
            <Input
              value={block.data.cta_text || ""}
              onChange={(e) => set("cta_text", e.target.value)}
              placeholder="Sprawdz termin i cene"
            />
            <p className="text-xs text-slate-500 mt-1">Przycisk przewija do kolejnego bloku na stronie.</p>
          </div>
          <div>
            <Label className="mb-2 block">Statystyki (wartosc|opis, po jednej na linie)</Label>
            <Textarea
              rows={3}
              value={statsText}
              onChange={(e) => set("stats", parsePairs(e.target.value, ["value", "label"]))}
              placeholder={"300|miejsc parkingowych\n24/7|dozor i monitoring"}
            />
          </div>
          <div>
            <Label className="mb-2 block">Kroki "jak to dziala" (tytul|opis, po jednym na linie)</Label>
            <Textarea
              rows={4}
              value={stepsText}
              onChange={(e) => set("steps", parsePairs(e.target.value, ["title", "description"]))}
              placeholder={"Rezerwujesz online|Wybierasz termin i placisz kartą lub BLIK-iem"}
            />
          </div>
          <div>
            <Label>Naglowek sekcji "dlaczego my"</Label>
            <Input value={block.data.why_us_heading || ""} onChange={(e) => set("why_us_heading", e.target.value)} />
          </div>
          <div>
            <Label className="mb-2 block">Atuty (ikona|tytul|opis, po jednym na linie)</Label>
            <p className="text-xs text-slate-500 mb-1">Dostepne ikony: shield, car, tag, plane, clock, check, phone, map-pin</p>
            <Textarea
              rows={5}
              value={whyText}
              onChange={(e) => set("why_us_items", parsePairs(e.target.value, ["icon", "title", "description"]))}
              placeholder={"shield|Dozor 24/7|Monitorowany, ogrodzony teren"}
            />
          </div>
        </div>
      );
    }

    case "faq": {
      const items = block.data.items || [];
      const text = items.map((it) => `${it.question || ""}|${it.answer || ""}`).join("\n");
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input value={block.data.heading || ""} onChange={(e) => set("heading", e.target.value)} />
          </div>
          <div>
            <Label className="mb-2 block">Pytania i odpowiedzi (pytanie|odpowiedz, po jednym na linie)</Label>
            <Textarea
              rows={8}
              value={text}
              onChange={(e) =>
                set(
                  "items",
                  e.target.value
                    .split("\n")
                    .filter((l) => l.trim().length)
                    .map((line) => {
                      const [question, answer] = line.split("|");
                      return { question: (question || "").trim(), answer: (answer || "").trim() };
                    })
                )
              }
              placeholder={"Co w przypadku opoznienia lotu?|Podajesz numer lotu przy rezerwacji — zadzwon do nas, jesli lot sie spozni, dopasujemy odbior."}
            />
            <p className="text-xs text-slate-500 mt-1">{items.length} pytan</p>
          </div>
        </div>
      );
    }

    case "whatsapp":
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input
              value={block.data.heading || ""}
              onChange={(e) => set("heading", e.target.value)}
              placeholder="Napisz do nas na WhatsApp"
            />
          </div>
          <div>
            <Label>Podtytul</Label>
            <Input value={block.data.subheading || ""} onChange={(e) => set("subheading", e.target.value)} />
          </div>
          <div>
            <Label>Numer telefonu (z kodem kraju)</Label>
            <Input
              value={block.data.phone || ""}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+48..."
            />
            <p className="text-xs text-slate-500 mt-1">Puste = uzyje telefonu z karty firmy</p>
          </div>
          <div>
            <Label>Wstepna wiadomosc (opcjonalna)</Label>
            <Textarea
              rows={2}
              value={block.data.message || ""}
              onChange={(e) => set("message", e.target.value)}
              placeholder="Dzien dobry, mam pytanie..."
            />
          </div>
        </div>
      );

    case "testimonials": {
      const items = block.data.items || [];
      const updateItem = (idx, newItem) => {
        const next = [...items];
        next[idx] = newItem;
        set("items", next);
      };
      const addItem = () => set("items", [...items, { name: "", text: "", rating: 5 }]);
      const removeItem = (idx) => set("items", items.filter((_, i) => i !== idx));
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input value={block.data.heading || ""} onChange={(e) => set("heading", e.target.value)} />
          </div>
          <div className="space-y-3">
            {items.map((it, i) => (
              <TestimonialItem
                key={i}
                item={it}
                onChange={(ni) => updateItem(i, ni)}
                onRemove={() => removeItem(i)}
              />
            ))}
            <Button variant="outline" size="sm" onClick={addItem} className="w-full">
              <Plus className="w-4 h-4 mr-1" /> Dodaj opinie
            </Button>
          </div>
        </div>
      );
    }

    case "social":
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input value={block.data.heading || ""} onChange={(e) => set("heading", e.target.value)} />
          </div>
          <div>
            <Label>Facebook URL</Label>
            <Input
              value={block.data.facebook || ""}
              onChange={(e) => set("facebook", e.target.value)}
              placeholder="https://facebook.com/..."
            />
          </div>
          <div>
            <Label>Instagram URL</Label>
            <Input
              value={block.data.instagram || ""}
              onChange={(e) => set("instagram", e.target.value)}
              placeholder="https://instagram.com/..."
            />
          </div>
          <div>
            <Label>TikTok URL</Label>
            <Input
              value={block.data.tiktok || ""}
              onChange={(e) => set("tiktok", e.target.value)}
              placeholder="https://tiktok.com/@..."
            />
          </div>
        </div>
      );

    case "opening":
    case "contact":
    case "map":
      return (
        <div className="space-y-3">
          <div>
            <Label>Naglowek</Label>
            <Input value={block.data.heading || ""} onChange={(e) => set("heading", e.target.value)} />
          </div>
          <p className="text-xs text-slate-500">
            Dane (godziny / telefon / lokalizacja) sa zaciagane z karty firmy w katalogu. Edytuj tam.
          </p>
        </div>
      );

    default:
      return <p className="text-sm text-slate-500">Brak edytora dla typu {block.type}</p>;
  }
}
